import React from "react";

export interface SliderStop {
  label: string; // the human-facing name for this position (e.g. "Nudge")
  value: number; // the number sent to the backend for this position
}

// Optimizer penalty scales: each soft-penalty knob is a 5-position slider so
// operators pick an intensity instead of guessing a raw number. Position 1 is
// always "off" (0), matching the old blank-input behavior. The balance scale and
// the time-window default (200) are the team's tuned values; the rest are starting
// points to be refined once there's data. Shared so the New Run form and the run
// result's input summary render the same labels.
export const BALANCE_STOPS: SliderStop[] = [
  { label: "Ignore", value: 0 },
  { label: "Nudge", value: 250 },
  { label: "Balance", value: 500 },
  { label: "Push", value: 1000 },
  { label: "Force", value: 1500 },
];
export const COMPACTNESS_STOPS: SliderStop[] = [
  { label: "Loose", value: 0 },
  { label: "Light", value: 1 },
  { label: "Moderate", value: 2 },
  { label: "Tight", value: 3 },
  { label: "Max", value: 4 },
];
export const TIME_WINDOW_STOPS: SliderStop[] = [
  { label: "Off", value: 0 },
  { label: "Soft", value: 50 },
  { label: "Default", value: 200 },
  { label: "Firm", value: 500 },
  { label: "Hard", value: 1000 },
];
export const END_TIME_STOPS: SliderStop[] = [
  { label: "Ignore", value: 0 },
  { label: "Light", value: 25 },
  { label: "Moderate", value: 50 },
  { label: "Strong", value: 100 },
  { label: "Strict", value: 200 },
];
export const OVERTIME_STOPS: SliderStop[] = [
  { label: "Lenient", value: 0 },
  { label: "Light", value: 25 },
  { label: "Moderate", value: 50 },
  { label: "Strong", value: 100 },
  { label: "Strict", value: 200 },
];
// Dump-fee rank weight: 5 positions from 50% to 100%; submitted value = position / 100.
// 100% (1.0) is the backend default (today's behavior). Lower = solver prefers nearer yards.
export const DUMP_FEE_WEIGHT_STOPS: SliderStop[] = [
  { label: "50%", value: 0.5 },
  { label: "62%", value: 0.625 },
  { label: "75%", value: 0.75 },
  { label: "87%", value: 0.875 },
  { label: "100%", value: 1.0 },
];

/** The nearest stop's label — used to render a raw penalty value as its intensity. */
export function stopLabel(value: number, stops: SliderStop[]): string {
  return stops[snapToStopIndex(value, stops)].label;
}

/**
 * Index of the stop whose value is closest to `value`. A copied run can carry any
 * number (operators used to type free-form values); on load it snaps to the nearest
 * position. Ties go to the lower index.
 */
export function snapToStopIndex(value: number, stops: SliderStop[]): number {
  let best = 0;
  let bestDist = Infinity;
  stops.forEach((s, i) => {
    const d = Math.abs(s.value - value);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  });
  return best;
}

/** The nearest stop's value — used to snap a prefilled number onto the scale. */
export function snapToStop(value: number, stops: SliderStop[]): number {
  return stops[snapToStopIndex(value, stops)].value;
}

interface Props {
  label: string;
  note?: React.ReactNode;
  stops: SliderStop[]; // five positions, low → high
  value: number;
  onChange: (value: number) => void;
}

/**
 * A labeled 5-position slider over a fixed set of {@link SliderStop}s. It maps each
 * position to a concrete number so operators pick an intensity ("Nudge", "Force")
 * instead of guessing a raw penalty value, while the backend still receives a number.
 */
export function PenaltySlider({ label, note, stops, value, onChange }: Props) {
  const index = snapToStopIndex(value, stops);
  const current = stops[index];

  return (
    <div className="config-field penalty-slider">
      <label>{label}</label>
      <input
        type="range"
        min={0}
        max={stops.length - 1}
        step={1}
        value={index}
        aria-label={label}
        aria-valuetext={`${current.label} (${current.value})`}
        onChange={(e) => onChange(stops[Number(e.target.value)].value)}
      />
      <div className="penalty-slider-ticks">
        {stops.map((s, i) => (
          <button
            type="button"
            key={s.label}
            className={`penalty-slider-tick${i === index ? " is-active" : ""}`}
            onClick={() => onChange(s.value)}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div className="penalty-slider-readout">
        <span className="penalty-slider-readout-label">{current.label}</span>
        <span className="penalty-slider-readout-value">→ {current.value}</span>
      </div>
      {note && <span className="config-note">{note}</span>}
    </div>
  );
}
